import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, ChevronRight } from 'lucide-react';
import { registryApi } from '../../services/registryService';
import TierBadge from './TierBadge';

export default function BlastRadiusModal({ serviceId, serviceName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await registryApi.getBlastRadius(serviceId);
        setData(result.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [serviceId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h3 className="text-base font-semibold text-gray-900">
              Blast Radius — {serviceName}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-5 py-4 flex-1">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600">Failed to load: {error}</p>
          )}

          {data && (
            <>
              {/* Summary */}
              <div className="flex gap-4 mb-4">
                <div className="flex-1 bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{data.direct_count}</p>
                  <p className="text-xs text-amber-600">Direct</p>
                </div>
                <div className="flex-1 bg-orange-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-orange-700">{data.transitive_count}</p>
                  <p className="text-xs text-orange-600">Transitive</p>
                </div>
                <div className="flex-1 bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-700">{data.affected_tiers?.length || 0}</p>
                  <p className="text-xs text-red-600">Tiers Affected</p>
                </div>
              </div>

              {/* Direct dependents */}
              {data.direct_dependents?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Direct Dependents</h4>
                  <div className="space-y-1">
                    {data.direct_dependents.map((dep) => (
                      <div key={dep.source_service_id} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg">
                        <TierBadge tier={dep.source_tier} />
                        <span className="text-sm text-gray-800">{dep.source_name}</span>
                        <ChevronRight className="w-3 h-3 text-gray-400 ml-auto" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Transitive dependents */}
              {data.transitive_dependents?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Transitive Dependents</h4>
                  <div className="space-y-1">
                    {data.transitive_dependents.map((dep) => (
                      <div key={dep.source_service_id} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg">
                        <TierBadge tier={dep.source_tier} />
                        <span className="text-sm text-gray-800">{dep.source_name}</span>
                        <span className="text-xs text-gray-400 ml-auto">depth {dep.depth}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {data.direct_count === 0 && data.transitive_count === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  No services depend on {serviceName}.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
